import { Box, Stack, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCreateKbFile,
  useMarkChatAsResolved,
  useSendChatMessage,
} from "controller";
import { MqttContext } from "controller/context/MqttContext";
import {
  fetchChatPage,
  useFetchMessageWindow,
} from "controller/hooks/chat/useChat";
import {
  chatMembersKeys,
  useGetChatMembers,
} from "controller/hooks/chat/useChatMembers";
import { useEditChatMessage } from "controller/hooks/chat/useEditChat";
import { useMqttChatRefetch } from "controller/hooks/chat/useMqttChatRefetch";
import {
  useGetPinnedMessages,
  usePinMessage,
  useUnpinMessage,
} from "controller/hooks/chat/usePinChat";
import {
  addThreadReplyToCache,
  threadMessagesKeys,
} from "controller/hooks/chat/useThreadMessages";
import { useGlobalStore } from "controller/store/globalStore";
import { useMainStore } from "controller/store/mainStore";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useAtomValue, useSetAtom } from "jotai";
import { KbArticleDrawer } from "pages/modules/kb/components/KbArticleDrawer";
import { createKbArticleFromHtml } from "pages/modules/kb/helpers/utils";
import { centralFolderList } from "pages/modules/kb/store";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import type { VirtuosoHandle } from "react-virtuoso";
import type {
  AccessSpecifier,
  ChatMember,
  ChatMemberEvent,
  ChatMetaData,
  ChatResultItem,
  FilterType,
  GetThreadMessagesResponse,
  MemberAddedEvent,
  MemberRemovedEvent,
  MemberTimelineMqttPayload,
  ThreadMessage,
} from "shared/types";
import { addMixpanelEvent, debugConsole } from "utilz/helpers";
import { getFullName } from "utilz/helpers/stringManipulator";
import { ChatHeader } from "./ChatHeader";
import { ChatInputSection } from "./ChatInputSection";
import { ChatMessageList } from "./ChatMessageList";
import { ChatSearchDrawer } from "./ChatSearchDrawer";
import { ChatMembersPopover } from "./components/ChatMembersPopover";
import { useDefaultChatSendMode } from "./hooks/useDefaultChatSendMode";
import { NewMessagesPill } from "./NewMessagesPill";
import { PinnedChatsDrawer } from "./PinnedChatsDrawer";
import { PinnedMessageBanner } from "./PinnedMessageBanner";
import {
  CHAT_PAGE_SIZE,
  chatEditingMessageId,
  chatEditorFocusRequest,
  chatEditorInjectRequest,
  chatFilter,
  chatIsUploadingImages,
  chatSendMode,
  chatThreadParentMessage,
  clearChatReplyToMessage,
  closeKbDrawer,
  incrementChatUnreadCount,
  isKbDrawerOpen,
  isPinnedDrawerOpen,
  isSearchDrawerOpen,
  kbArticleId,
  openKbDrawer,
  requestChatEditorFocus,
  requestThreadEditorFocus,
  resetChatStore,
  resetChatUnreadCount,
  setChatEditingMessageId,
  setChatFilter,
  setChatGroupedMessages,
  setChatMembers,
  setChatReplyToMessage,
  setModeBannerVisible,
  setPinnedDrawerOpen,
  setSearchDrawerOpen,
  useChatMessageStore,
} from "./store";
import type { ChatMessageInputRef } from "./TipTapChatInput";
import { removeChatDraft } from "./utils/chatDraftStorage";
import { buildFlatChatList } from "./utils/flattenForVirtuoso";
import { scrollToMessage } from "./utils/scrollToMessage";
import { stripHtml } from "./utils/stripHtml";
import { groupMessagesByDate } from "./utils/transformMessages";

dayjs.extend(relativeTime);

const MAX_PINNED_MESSAGES = 10;

function buildPinExcerpt(message: ChatResultItem): string {
  const textExcerpt = stripHtml(message.message).slice(0, 300);
  if (textExcerpt) return textExcerpt;
  if (!message.attachments?.length) return "";
  const firstName = message.attachments[0].file_name;
  return message.attachments.length > 1
    ? `${firstName} +${message.attachments.length - 1} more`
    : firstName;
}

function toThreadMessage(savedChat: ChatResultItem): ThreadMessage {
  return {
    _id: savedChat._id,
    chat_id: savedChat.chat_id,
    message: savedChat.message,
    thread_id: savedChat.thread_id ?? "",
    excerpt: savedChat.excerpt ?? null,
    access_specifier: savedChat.access_specifier,
    sender: savedChat.sender,
    attachments: savedChat.attachments ?? [],
    created_at: savedChat.created_at,
    message_platform: savedChat.message_platform,
    message_type: savedChat.message_type,
    editing: savedChat.editing,
    mark_as_resolved: savedChat.mark_as_resolved,
    send_as_email: savedChat.send_as_email,
  };
}

function handleMqttThreadReply(
  savedChat: ChatResultItem,
  updateMessage: (
    id: string,
    updater: (msg: ChatResultItem) => ChatResultItem,
  ) => void,
  messageMapRef: React.MutableRefObject<Map<string, ChatResultItem>>,
  openThreadId: string | null | undefined,
  queryClient: import("@tanstack/react-query").QueryClient,
) {
  const parentId = savedChat.parent_message_id;
  if (!parentId) return;
  if (!savedChat.thread_id) return;
  if (savedChat.is_thread_root) return;

  const threadId = savedChat.thread_id;

  // Increment thread_reply_count on the parent and on every existing reply
  // in the same thread, so the badge can render on any of them consistently.
  for (const existing of messageMapRef.current.values()) {
    const isParent =
      existing.chat_id === parentId || existing.thread_id === threadId;
    if (!isParent) continue;
    updateMessage(existing.chat_id, (msg) => {
      if (msg.chat_id === parentId) {
        return {
          ...msg,
          is_thread_root: true,
          thread_id: savedChat.thread_id ?? msg.thread_id,
          thread_reply_count: (msg.thread_reply_count ?? 0) + 1,
          thread_last_reply_at: savedChat.created_at,
        };
      }
      return {
        ...msg,
        thread_reply_count: (msg.thread_reply_count ?? 0) + 1,
        thread_last_reply_at: savedChat.created_at,
      };
    });
  }

  if (openThreadId && openThreadId === savedChat.thread_id) {
    addThreadReplyToCache(
      queryClient,
      openThreadId,
      toThreadMessage(savedChat),
    );
  }
}

function memberTimelinePayloadToChatItem(
  payload: MemberTimelineMqttPayload,
): ChatResultItem {
  return {
    _id: payload.chat_id,
    chat_id: payload.chat_id,
    service_id: payload.service_id,
    message: payload.message,
    message_type: payload.message_type,
    message_platform: "web",
    sender: payload.sender,
    timeline_meta: payload.timeline_meta,
    access_specifier: "public",
    attachments: [],
    mark_as_resolved: false,
    created_at: payload.timestamp,
    editing: { is_edited: false },
    thread_id: payload.thread_id ?? null,
    parent_message_id: null,
    is_thread_root: false,
  };
}

function handleMqttMemberTimeline(
  chatItem: ChatResultItem,
  openThreadId: string | null | undefined,
  queryClient: import("@tanstack/react-query").QueryClient,
) {
  if (openThreadId && openThreadId === chatItem.thread_id) {
    addThreadReplyToCache(queryClient, openThreadId, toThreadMessage(chatItem));
  }
}

/** Normalize member_added MQTT event (flat or batch) into ChatMember[] */
function parseMemberAddedEvent(event: MemberAddedEvent): ChatMember[] {
  if (event.members) {
    return event.members.map((m) => ({
      user_id: m.mapping_id,
      display_name: m.display_name ?? null,
      member_type: m.user_type,
      member_source: m.member_source,
      email: null,
      profile_pic: m.profile_pic ?? null,
    }));
  }

  return [
    {
      user_id: event.user_id ?? "",
      display_name: event.display_name ?? null,
      member_type: event.member_type ?? "customer",
      // Bug #17: CS's publishMemberEvent expands batched core-add events
      // into per-member flat events (Block 26 / Bug #2 shape normalization),
      // so the wire payload for an SD assignee-add or manager-add carries
      // `event.member_source: "core"` at top level. Previously the hardcoded
      // "cc" here mis-classified assigned techs as removable CC members until
      // a full listMembers refetch (F5). Honor the incoming value; fall back
      // to "cc" only when the event omits member_source (legacy CC-add flows
      // via addMembers: email reconcile, @mention, manual chat-panel adds).
      member_source: event.member_source ?? "cc",
      email: event.email ?? null,
      profile_pic: event.profile_pic ?? null,
    },
  ];
}

/**
 * Upsert members into an existing list, deduped by mapping_id (user_id).
 *
 * Core wins over cc: the same mapping_id can move `cc → core` (a CC'd tech who
 * then gets assigned re-fires member_added with member_source: "core"). When
 * that happens we replace the stale `cc` entry so the member stops rendering as
 * a removable CC participant. Otherwise a duplicate is skipped. Returns the
 * original reference when nothing changed to avoid needless re-renders.
 */
function upsertMembers(
  existing: ChatMember[],
  incoming: ChatMember[],
): ChatMember[] {
  const byId = new Map(existing.map((m) => [m.user_id, m]));
  let changed = false;

  for (const member of incoming) {
    const current = byId.get(member.user_id);
    if (!current) {
      byId.set(member.user_id, member);
      changed = true;
    } else if (
      member.member_source === "core" &&
      current.member_source !== "core"
    ) {
      byId.set(member.user_id, member);
      changed = true;
    }
  }

  return changed ? Array.from(byId.values()) : existing;
}

export const Chat = ({
  serviceType,
  serviceId,
  serviceSource,
  isDisabled,
  isTriageInProgress,
  metaData,
  section = "serviceDetails",
  onClose,
  onFullscreenClick,
  onThreadClick,
  onThreadClose,
}: {
  isDisabled: boolean;
  isTriageInProgress?: boolean;
  serviceType: "ticket" | "task";
  serviceId: string;
  serviceSource?: string;
  metaData?: ChatMetaData;
  section?: "serviceDetails" | "messageBar";
  onClose?: () => void;
  onFullscreenClick?: () => void;
  onThreadClick?: (message: ChatResultItem) => void;
  onThreadClose?: () => void;
}) => {
  const editorRef = useRef<ChatMessageInputRef>(null);
  const bannerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(
    null,
  ) as React.RefObject<VirtuosoHandle>;
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const pendingScrollChatId = useRef<string | null>(null);
  const forceScrollOnAppendRef = useRef(false);
  const fetchingPagesRef = useRef<Set<number>>(new Set());
  const theme = useTheme();
  const resourceId = useGlobalStore((state) => state.resourceId);

  // ─── Message Store ──────────────────────────────────────────────
  const store = useChatMessageStore();
  const {
    messageMap,
    messageMapRef,
    sortedMessages,
    lastAction,
    loadedPagesRef,
    reachedOldest,
    reachedOldestRef,
    isLoadingInitial,
    isLoadingMore,
    gapFillAbortRef,
    setIsLoadingInitial,
    setIsLoadingMore,
    addPageResult,
    addWindowResult,
    addMqttMessage,
    updateMessage,
    reset: resetStore,
    getNextOlderPage,
  } = store;

  // ─── Jotai Atoms (shared chat state) ────────────────────────────
  const sendMessageAccessSpecifier = useAtomValue(chatSendMode);
  const filter = useAtomValue(chatFilter);
  const filterAccessSpecifier = filter.accessSpecifier;
  const filterMarkAsResolved = filter.markAsResolved;
  const isUploadingImages = useAtomValue(chatIsUploadingImages);
  const setBannerVisible = useSetAtom(setModeBannerVisible);
  const pinnedDrawerOpen = useAtomValue(isPinnedDrawerOpen);
  const setDrawerOpen = useSetAtom(setPinnedDrawerOpen);
  const searchDrawerOpen = useAtomValue(isSearchDrawerOpen);
  const setSearchOpen = useSetAtom(setSearchDrawerOpen);
  const kbDrawerOpen = useAtomValue(isKbDrawerOpen);
  const newKbArticleId = useAtomValue(kbArticleId);
  const openKb = useSetAtom(openKbDrawer);
  const closeKb = useSetAtom(closeKbDrawer);
  const setGroupedMessages = useSetAtom(setChatGroupedMessages);
  const setAtomMembers = useSetAtom(setChatMembers);
  const setReplyTo = useSetAtom(setChatReplyToMessage);
  const clearReplyAtom = useSetAtom(clearChatReplyToMessage);
  const resetChatAtoms = useSetAtom(resetChatStore);
  const threadParentMessage = useAtomValue(chatThreadParentMessage);
  const editingMessageId = useAtomValue(chatEditingMessageId);
  const setEditingId = useSetAtom(setChatEditingMessageId);
  const chatFocusReq = useAtomValue(chatEditorFocusRequest);
  const chatInjectReq = useAtomValue(chatEditorInjectRequest);
  const requestChatFocus = useSetAtom(requestChatEditorFocus);
  const requestThreadFocus = useSetAtom(requestThreadEditorFocus);

  // ─── Edit Message Mutation ───────────────────────────────────
  const { mutateAsync: editMessage } = useEditChatMessage();

  // ─── Local State (transient, Chat-only) ───────────────────────
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [participantsAnchorEl, setParticipantsAnchorEl] =
    useState<HTMLElement | null>(null);
  const participantsPopoverOpen = Boolean(participantsAnchorEl);
  const [highlightedChatId, setHighlightedChatId] = useState<string | null>(
    null,
  );
  const [targetMessageId, setTargetMessageId] = useState<string | undefined>();
  const isAtBottomRef = useRef(true);
  const incrementUnread = useSetAtom(incrementChatUnreadCount);
  const resetUnread = useSetAtom(resetChatUnreadCount);

  // ─── External State & Hooks ─────────────────────────────────────
  const feedback = useMainStore((state) => state.feedback);
  const rating = useMainStore((state) => state.rating);
  const mspId = useGlobalStore((state) => state.mspId);
  const {
    mqttMessage,
    subscribeRoom,
    unSubscribeRoom,
    isConnected,
    setRefetchHandler,
  } = useContext(MqttContext);
  const queryClient = useQueryClient();

  // Recover missed chat messages when the broker reports a non-resumed session.
  useMqttChatRefetch(setRefetchHandler);
  useDefaultChatSendMode();
  const centralKbFolder = useAtomValue(centralFolderList);

  // ─── Window fetch for scroll-to-pinned ──────────────────────────
  const { data: windowData } = useFetchMessageWindow({
    serviceType,
    serviceId,
    fetcherId: resourceId || "",
    accessSpecifier: filterAccessSpecifier || undefined,
    filterMarkAsResolved,
    messageId: targetMessageId,
    enabled: Boolean(targetMessageId),
  });

  const { mutateAsync: sendMessage } = useSendChatMessage();
  const { mutateAsync: markAsResolved } = useMarkChatAsResolved();

  const { data: rawPinnedMessages } = useGetPinnedMessages({
    serviceId,
    type: "resource",
  });
  const pinnedMessages = Array.isArray(rawPinnedMessages)
    ? rawPinnedMessages
    : [];
  const { mutateAsync: pinMessage } = usePinMessage();
  const { mutateAsync: unpinMessage } = useUnpinMessage();

  const pinnedChatIdSet = useMemo(
    () => new Set(pinnedMessages.map((pm) => pm.chat_id)),
    [pinnedMessages],
  );

  const { mutateAsync: createKbFile, isLoading: isKbCreating } =
    useCreateKbFile();

  // ─── Derive grouped messages (for ChatHeader) ──────────────────
  const groupedMessages = useMemo(() => {
    if (sortedMessages.length === 0) return null;
    return groupMessagesByDate(sortedMessages);
  }, [sortedMessages]);

  // Sync derived state to Jotai atoms for child component access
  useEffect(() => {
    setGroupedMessages(groupedMessages);
  }, [groupedMessages, setGroupedMessages]);

  // ─── Fetch chat members via dedicated API ────────────────────
  const { data: chatMembersData } = useGetChatMembers(serviceId);

  useEffect(() => {
    if (chatMembersData) {
      setAtomMembers(chatMembersData);
    }
  }, [chatMembersData, setAtomMembers]);

  // ─── Flat chat list for Virtuoso ──────────────────────────────
  const flatChatList = useMemo(
    () => buildFlatChatList(sortedMessages),
    [sortedMessages],
  );

  const INITIAL_FIRST_INDEX = 100_000;
  const [firstItemIndex, setFirstItemIndex] = useState(INITIAL_FIRST_INDEX);
  const prevFlatLengthRef = useRef(0);

  // Adjust firstItemIndex when items are prepended (older pages loaded)
  useEffect(() => {
    const currentLength = flatChatList.length;
    const prevLength = prevFlatLengthRef.current;

    if (prevLength > 0 && currentLength > prevLength) {
      const diff = currentLength - prevLength;
      // Only shift firstItemIndex for prepend/window actions (not appends via MQTT)
      if (lastAction === "prepend" || lastAction === "window") {
        setFirstItemIndex((prev) => prev - diff);
      }
    }

    prevFlatLengthRef.current = currentLength;
  }, [flatChatList, lastAction]);

  // Force scroll to bottom when the current user just sent a message
  // (regardless of whether they were scrolled up).
  useEffect(() => {
    if (lastAction !== "append") return;
    const lastItem = flatChatList[flatChatList.length - 1];
    if (lastItem?.kind !== "message") return;
    const isOwnMessage = lastItem.message.sender.mapping_id === resourceId;

    if (forceScrollOnAppendRef.current && isOwnMessage) {
      forceScrollOnAppendRef.current = false;
      virtuosoRef.current?.scrollToIndex({
        index: "LAST",
        align: "end",
      });
      return;
    }

    // Incoming message from someone else while user is scrolled up:
    // increment the unread counter so the "New messages" pill appears.
    if (isOwnMessage) return;
    if (isAtBottomRef.current) return;
    incrementUnread();
  }, [flatChatList, lastAction, resourceId, incrementUnread]);

  const handleAtBottomStateChange = useCallback(
    (atBottom: boolean) => {
      isAtBottomRef.current = atBottom;
      if (atBottom) {
        resetUnread();
      }
    },
    [resetUnread],
  );

  const lastUpdated = useMemo(() => {
    if (sortedMessages.length === 0) return undefined;
    return sortedMessages[0].created_at;
  }, [sortedMessages]);

  // ─── Initial page load ──────────────────────────────────────────
  useEffect(() => {
    if (!serviceId) return;
    if (!resourceId) return;

    resetStore();
    setFirstItemIndex(INITIAL_FIRST_INDEX);
    prevFlatLengthRef.current = 0;
    fetchingPagesRef.current.clear();

    const loadInitialPage = async () => {
      try {
        const data = await fetchChatPage({
          serviceType,
          serviceId,
          fetcherId: resourceId,
          page: 1,
          limit: CHAT_PAGE_SIZE,
          accessSpecifier: filterAccessSpecifier || undefined,
          filterMarkAsResolved,
        });
        addPageResult(1, data, CHAT_PAGE_SIZE);
      } catch (err: unknown) {
        debugConsole(err);
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadInitialPage();
  }, [
    serviceId,
    resourceId,
    filterAccessSpecifier,
    filterMarkAsResolved,
    serviceType,
    resetStore,
    addPageResult,
    setIsLoadingInitial,
  ]);

  // ─── Banner timeout ─────────────────────────────────────────────

  const showModeBanner = useCallback(() => {
    setBannerVisible(true);
    if (bannerTimeoutRef.current) {
      clearTimeout(bannerTimeoutRef.current);
    }
    bannerTimeoutRef.current = setTimeout(() => {
      setBannerVisible(false);
    }, 3000);
  }, [setBannerVisible]);

  const clearBannerTimeout = useCallback(() => {
    if (bannerTimeoutRef.current) {
      clearTimeout(bannerTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    showModeBanner();
    return clearBannerTimeout;
  }, [showModeBanner, clearBannerTimeout]);

  // ─── Scroll-to-pinned helpers ───────────────────────────────────

  const triggerHighlight = useCallback((chatId: string) => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setHighlightedChatId(chatId);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedChatId(null);
    }, 2000);
  }, []);

  // Pending scroll effect — scroll to message after data loads
  useEffect(() => {
    const chatId = pendingScrollChatId.current;
    if (!chatId) return;
    if (!messageMap.has(chatId)) return;

    // Small delay to let Virtuoso render the newly added items
    const timerId = setTimeout(() => {
      const scrolled = scrollToMessage(virtuosoRef, flatChatList, chatId);
      if (scrolled) {
        triggerHighlight(chatId);
      }
      pendingScrollChatId.current = null;
    }, 100);
    return () => clearTimeout(timerId);
  }, [messageMap, flatChatList, triggerHighlight]);

  // ─── Window fetch → merge into store + background gap fill ──────

  const fetchParamsRef = useRef({
    serviceType,
    serviceId,
    fetcherId: resourceId || "",
    accessSpecifier: filterAccessSpecifier || undefined,
    filterMarkAsResolved,
  });
  fetchParamsRef.current = {
    serviceType,
    serviceId,
    fetcherId: resourceId || "",
    accessSpecifier: filterAccessSpecifier || undefined,
    filterMarkAsResolved,
  };

  const fetchGapPage = useCallback(
    async (page: number, signal: AbortSignal): Promise<"continue" | "stop"> => {
      const data = await fetchChatPage({
        ...fetchParamsRef.current,
        page,
        limit: CHAT_PAGE_SIZE,
      });
      if (signal.aborted) return "stop";

      addPageResult(page, data, CHAT_PAGE_SIZE);

      if (data.messages.length < CHAT_PAGE_SIZE) return "stop";
      if (data.messages.every((msg) => messageMapRef.current.has(msg.chat_id)))
        return "stop";
      return "continue";
    },
    [addPageResult, messageMapRef],
  );

  const fillGapInBackground = useCallback(async () => {
    gapFillAbortRef.current?.abort();
    const controller = new AbortController();
    gapFillAbortRef.current = controller;

    let page = getNextOlderPage() ?? 2;

    while (!controller.signal.aborted) {
      if (loadedPagesRef.current.has(page)) {
        page++;
        continue;
      }

      try {
        const result = await fetchGapPage(page, controller.signal);
        if (result === "stop") break;
        page++;
      } catch {
        break;
      }

      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }, [gapFillAbortRef, getNextOlderPage, loadedPagesRef, fetchGapPage]);

  useEffect(() => {
    if (!windowData) return;
    if (!targetMessageId) return;

    addWindowResult(windowData);
    pendingScrollChatId.current = targetMessageId;
    setTargetMessageId(undefined);

    fillGapInBackground();
  }, [windowData, targetMessageId, addWindowResult, fillGapInBackground]);

  // ─── MQTT real-time messages ────────────────────────────────────
  // Use a ref for threadParentMessage to avoid re-running the effect
  // (and re-processing the last MQTT message) when the user opens a thread.
  const threadParentRef = useRef(threadParentMessage);
  threadParentRef.current = threadParentMessage;

  useEffect(() => {
    if (!mqttMessage) return;

    const newMessage = JSON.parse(mqttMessage.message);
    const topics = [
      `${serviceType}s/${mspId}/${serviceId}`,
      `${serviceType}s/${mspId}/${serviceId}/private`,
      `${serviceType}s/${mspId}/${serviceId}/resource/${resourceId}`,
    ];

    if (!topics.includes(mqttMessage.topic)) return;

    // ── Regular chat messages (savedChat wrapper) ──
    const savedChat = newMessage?.savedChat;
    if (savedChat) {
      addMqttMessage(savedChat);
      handleMqttThreadReply(
        savedChat,
        updateMessage,
        messageMapRef,
        threadParentRef.current?.thread_id,
        queryClient,
      );
      return;
    }

    // ── message_edited event ──
    if (newMessage?.type === "message_edited" && newMessage?.data) {
      const editData = newMessage.data;
      updateMessage(editData.chat_id, (msg) => ({
        ...msg,
        message: editData.message,
        editing: {
          is_edited: true,
          edited_at: editData.editing.edited_at,
          edited_by: editData.editing.edited_by,
        },
      }));

      // Also update thread cache if thread_id is set
      if (editData.thread_id) {
        const threadId = editData.thread_id;
        queryClient.setQueryData<GetThreadMessagesResponse>(
          threadMessagesKeys.detail(threadId),
          (oldData) => {
            if (!oldData?.data) return oldData;
            return {
              ...oldData,
              data: {
                ...oldData.data,
                messages: oldData.data.messages.map((msg) =>
                  msg.chat_id === editData.chat_id
                    ? {
                        ...msg,
                        message: editData.message,
                        editing: {
                          is_edited: true as const,
                          edited_at: editData.editing.edited_at,
                          edited_by: editData.editing.edited_by,
                        },
                      }
                    : msg,
                ),
                parent_message:
                  oldData.data.parent_message.chat_id === editData.chat_id
                    ? {
                        ...oldData.data.parent_message,
                        message: editData.message,
                        editing: {
                          is_edited: true as const,
                          edited_at: editData.editing.edited_at,
                          edited_by: editData.editing.edited_by,
                        },
                      }
                    : oldData.data.parent_message,
              },
            };
          },
        );
      }
      return;
    }

    // ── member_timeline events (flat payload) ──
    if (newMessage?.event === "member_timeline") {
      const chatItem = memberTimelinePayloadToChatItem(newMessage);
      if (newMessage.thread_id) {
        // Thread-scoped: add to thread panel only
        handleMqttMemberTimeline(
          chatItem,
          threadParentRef.current?.thread_id,
          queryClient,
        );
      } else {
        // Ticket-level: add to main chat only
        addMqttMessage(chatItem);
      }
    }
  }, [
    mqttMessage,
    serviceId,
    resourceId,
    serviceType,
    mspId,
    addMqttMessage,
    updateMessage,
    messageMapRef,
    queryClient,
  ]);

  // ─── MQTT members topic subscription ──────────────────────────
  useEffect(() => {
    if (!isConnected) return;

    const membersTopic = `${serviceType}s/${mspId}/${serviceId}/members`;
    subscribeRoom(membersTopic);

    return () => {
      unSubscribeRoom(membersTopic);
    };
  }, [
    isConnected,
    serviceType,
    mspId,
    serviceId,
    subscribeRoom,
    unSubscribeRoom,
  ]);

  // ─── MQTT real-time member updates ────────────────────────────
  useEffect(() => {
    if (!mqttMessage) return;

    const membersTopic = `${serviceType}s/${mspId}/${serviceId}/members`;
    if (mqttMessage.topic !== membersTopic) return;

    try {
      const event: ChatMemberEvent = JSON.parse(mqttMessage.message);

      if (event.event === "member_added") {
        const newMembers = parseMemberAddedEvent(event as MemberAddedEvent);
        setAtomMembers((prev) => upsertMembers(prev, newMembers));
      } else if (event.event === "member_removed") {
        const removedEvent = event as MemberRemovedEvent;
        setAtomMembers((prev) =>
          prev.filter((m) => m.user_id !== removedEvent.user_id),
        );
      }

      queryClient.setQueryData<ChatMember[]>(
        chatMembersKeys.list(serviceId),
        (old) => {
          if (!old) return old;
          if (event.event === "member_added") {
            const newMembers = parseMemberAddedEvent(event as MemberAddedEvent);
            return upsertMembers(old, newMembers);
          }
          if (event.event === "member_removed") {
            const removedEvent = event as MemberRemovedEvent;
            return old.filter((m) => m.user_id !== removedEvent.user_id);
          }
          return old;
        },
      );
    } catch (error) {
      console.error("Failed to parse MQTT member event:", error);
    }
  }, [mqttMessage, serviceType, mspId, serviceId, setAtomMembers, queryClient]);

  // ─── Cleanup on unmount ─────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      gapFillAbortRef.current?.abort();
      resetChatAtoms();
    };
  }, [gapFillAbortRef, resetChatAtoms]);

  // ─── Pinned message click ──────────────────────────────────────
  const handlePinnedMessageClick = useCallback(
    (chatId: string) => {
      setDrawerOpen(false);

      if (messageMap.has(chatId)) {
        setTimeout(() => {
          const scrolled = scrollToMessage(virtuosoRef, flatChatList, chatId);
          if (scrolled) {
            triggerHighlight(chatId);
          }
        }, 100);
        return;
      }

      pendingScrollChatId.current = chatId;
      setTargetMessageId(chatId);
    },
    [setDrawerOpen, messageMap, flatChatList, triggerHighlight],
  );

  // ─── Load more (scroll up → older messages) ────────────────────
  const handleLoadMore = useCallback(async () => {
    if (reachedOldestRef.current) return;
    if (isLoadingMore) return;

    const nextPage = getNextOlderPage();
    if (nextPage === null) return;
    if (fetchingPagesRef.current.has(nextPage)) return;

    fetchingPagesRef.current.add(nextPage);
    setIsLoadingMore(true);

    try {
      const data = await fetchChatPage({
        ...fetchParamsRef.current,
        page: nextPage,
        limit: CHAT_PAGE_SIZE,
      });
      addPageResult(nextPage, data, CHAT_PAGE_SIZE);
    } catch (err) {
      debugConsole(err);
    } finally {
      fetchingPagesRef.current.delete(nextPage);
      setIsLoadingMore(false);
    }
  }, [
    reachedOldestRef,
    isLoadingMore,
    getNextOlderPage,
    setIsLoadingMore,
    addPageResult,
  ]);

  // ─── Callbacks ──────────────────────────────────────────────────

  const handleSendMessage = useCallback(
    async ({
      type,
      messageText,
      attachments,
      mentionedUsers,
      mentionedEmails,
      parentMessageId,
      excerpt,
    }: {
      type: AccessSpecifier;
      messageText: string;
      attachments?: Array<{
        mapping_id: string;
        file_type: string;
        url: string;
        original_name: string;
      }>;
      mentionedUsers?: string[];
      mentionedEmails?: string[];
      parentMessageId?: string;
      excerpt?: string;
    }) => {
      if (isSendingMessage) return;
      setIsSendingMessage(true);

      const trimmedValue = messageText.trim();
      if (
        (trimmedValue.length > 0 || (attachments && attachments.length > 0)) &&
        !isDisabled
      ) {
        forceScrollOnAppendRef.current = true;
        await sendMessage({
          message: trimmedValue,
          accessSpecifier: type,
          serviceId,
          serviceType,
          messageType: "normal",
          ...(attachments && attachments.length > 0 && { attachments }),
          ...(mentionedUsers?.length && { mentioned_users: mentionedUsers }),
          ...(mentionedEmails?.length && { mentioned_emails: mentionedEmails }),
          ...(parentMessageId && { parent_message_id: parentMessageId }),
          ...(excerpt && { excerpt }),
        });

        // Clear reply on successful send
        if (parentMessageId) {
          clearReplyAtom();
        }

        if (editorRef.current) {
          debugConsole("clear called");
          editorRef.current.clear();
        }
        // Draft has been sent — drop the persisted copy for this service.
        removeChatDraft(serviceId);
        addMixpanelEvent({
          event: "chat_message_access_specifier",
          data: { access_specifier: type },
        });
      }
      setIsSendingMessage(false);
    },
    [
      isSendingMessage,
      isDisabled,
      sendMessage,
      serviceId,
      serviceType,
      clearReplyAtom,
    ],
  );

  const handleMarkAsResolved = useCallback(
    async (chatId: string, newValue: boolean, _key: string) => {
      const resolved = await markAsResolved({ chatId, newValue });
      updateMessage(chatId, (msg) => ({
        ...msg,
        mark_as_resolved: resolved.mark_as_resolved,
      }));
    },
    [markAsResolved, updateMessage],
  );

  const handlePinMessage = useCallback(
    async (message: ChatResultItem, isPinned: boolean) => {
      try {
        if (isPinned) {
          await unpinMessage({
            chatId: message.chat_id,
            type: "resource",
            serviceId,
          });
          return;
        }
        if (pinnedMessages.length >= MAX_PINNED_MESSAGES) {
          toast.error(
            "You have reached the maximum of 10 pinned conversations. Unpin one to add another.",
          );
          return;
        }
        await pinMessage({
          chatId: message.chat_id,
          type: "resource",
          excerpt: buildPinExcerpt(message),
          serviceId,
          message,
        });
      } catch (error: unknown) {
        if (error) {
          toast.error(
            "You have reached the maximum of 20 pinned conversations. Unpin one to add another.",
          );
        }
      }
    },
    [pinMessage, unpinMessage, serviceId, pinnedMessages.length],
  );

  // Focus main chat editor when a focus request is dispatched
  useEffect(() => {
    if (chatFocusReq === 0) return;
    editorRef.current?.focus();
  }, [chatFocusReq]);

  // Append content to the main chat editor when an inject request is dispatched.
  // Existing typed text is preserved (content lands at the end).
  useEffect(() => {
    if (!chatInjectReq) return;
    const editor = editorRef.current?.getEditor();
    if (!editor) return;
    // Convert plain-text newlines to <br> so line breaks survive in the HTML editor
    const html = chatInjectReq.content;
    const chain = editor.chain().focus("end");
    if (!editor.isEmpty) chain.insertContent("<br>");
    chain.insertContent(html).run();
  }, [chatInjectReq]);

  const handleReplyClick = useCallback(
    (message: ChatResultItem) => {
      const messageThreadId = message.thread_id ?? null;
      const openThreadId = threadParentRef.current?.thread_id ?? null;
      const isThreadPanelOpen = Boolean(threadParentRef.current);

      // Case A: same thread already open → just focus thread editor
      if (
        isThreadPanelOpen &&
        messageThreadId &&
        messageThreadId === openThreadId
      ) {
        requestThreadFocus();
        return;
      }

      // Case B: message belongs to a thread (different thread, or panel closed)
      if (messageThreadId && onThreadClick) {
        clearReplyAtom();
        const parentMsg = message.parent_message_id
          ? messageMapRef.current.get(message.parent_message_id)
          : undefined;
        onThreadClick(parentMsg ?? message);
        requestThreadFocus();
        return;
      }

      // Case C: non-threaded message → close thread (if open), quoted reply, focus main editor
      if (isThreadPanelOpen && onThreadClose) {
        onThreadClose();
      }
      setReplyTo({
        chatId: message.chat_id,
        message: message.message,
        senderDisplayName: getFullName(
          message.sender.first_name,
          null,
          message.sender.last_name,
        ),
        accessSpecifier: message.access_specifier,
      });
      requestChatFocus();
    },
    [
      setReplyTo,
      clearReplyAtom,
      onThreadClick,
      onThreadClose,
      messageMapRef,
      requestThreadFocus,
      requestChatFocus,
    ],
  );

  // ─── Edit Handlers ──────────────────────────────────────────────

  const handleEditClick = useCallback(
    (message: ChatResultItem) => {
      setEditingId(message.chat_id);
    },
    [setEditingId],
  );

  const handleEditSave = useCallback(
    async (chatId: string, newContent: string) => {
      try {
        const result = await editMessage({
          serviceType,
          serviceId,
          chatId,
          message: newContent,
        });
        updateMessage(chatId, (msg) => ({
          ...msg,
          message: result.message,
          editing: {
            is_edited: true,
            edited_at: result.editing.edited_at,
            edited_by: result.editing.edited_by,
          },
        }));
        setEditingId(null);
      } catch {
        // Error handled by apiErrorHandler
      }
    },
    [editMessage, serviceType, serviceId, updateMessage, setEditingId],
  );

  const handleEditCancel = useCallback(() => {
    setEditingId(null);
  }, [setEditingId]);

  const handleUnpinFromDrawer = useCallback(
    async (chatId: string) => {
      try {
        await unpinMessage({ chatId, type: "resource", serviceId });
        if (pinnedMessages.length <= 1) {
          setDrawerOpen(false);
        }
      } catch {
        // silently fail
      }
    },
    [unpinMessage, serviceId, pinnedMessages.length, setDrawerOpen],
  );

  const dispatchFilter = useSetAtom(setChatFilter);
  const handleFilterChange = useCallback(
    (type?: FilterType) => {
      dispatchFilter(type ?? "all");
    },
    [dispatchFilter],
  );

  const handleAddToKB = useCallback(
    async (
      messageHtml: string,
      attachments?: Array<{
        file_name: string;
        file_type: string;
        url: string;
      }>,
    ) => {
      try {
        if (isKbCreating) return;
        const ticketSummary = metaData?.ticket_summary || "Untitled";
        const { content, update, excerpt } = createKbArticleFromHtml(
          messageHtml,
          ticketSummary,
          attachments,
        );

        const fileData = await createKbFile({
          name: ticketSummary,
          parent_id: centralKbFolder?.[0]?.mapping_id || null,
          content_type: "JSON",
          content,
          excerpt,
          yjs_update: update,
        });

        openKb(fileData?.[0]?.article_id);
      } catch {
        toast.error("Failed to create KB article");
      }
    },
    [
      createKbFile,
      centralKbFolder,
      metaData?.ticket_summary,
      isKbCreating,
      openKb,
    ],
  );

  const handleInputSend = useCallback(
    ({
      messageText,
      attachmentArray,
      mentionedUsers,
      mentionedEmails,
      parentMessageId,
      excerpt,
    }: {
      messageText: string;
      attachmentArray?: Array<{
        mapping_id: string;
        file_type: string;
        url: string;
        original_name: string;
      }>;
      mentionedUsers?: string[];
      mentionedEmails?: string[];
      parentMessageId?: string;
      excerpt?: string;
    }) => {
      if (!(isDisabled || isSendingMessage || isUploadingImages)) {
        handleSendMessage({
          messageText,
          type: sendMessageAccessSpecifier,
          attachments: attachmentArray,
          mentionedUsers,
          mentionedEmails,
          parentMessageId,
          excerpt,
        });
      }
    },
    [
      isDisabled,
      isSendingMessage,
      isUploadingImages,
      sendMessageAccessSpecifier,
      handleSendMessage,
    ],
  );

  const handleCloseKbDrawer = useCallback(() => {
    closeKb();
  }, [closeKb]);

  const handleOpenPinnedDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, [setDrawerOpen]);

  const handleClosePinnedDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, [setDrawerOpen]);

  const handleOpenSearchDrawer = useCallback(() => {
    setSearchOpen(true);
  }, [setSearchOpen]);

  const handleCloseSearchDrawer = useCallback(() => {
    setSearchOpen(false);
  }, [setSearchOpen]);

  const handleSearchResultClick = useCallback(
    (chatId: string) => {
      setSearchOpen(false);

      if (messageMap.has(chatId)) {
        setTimeout(() => {
          const scrolled = scrollToMessage(virtuosoRef, flatChatList, chatId);
          if (scrolled) {
            triggerHighlight(chatId);
          }
        }, 100);
        return;
      }

      pendingScrollChatId.current = chatId;
      setTargetMessageId(chatId);
    },
    [setSearchOpen, messageMap, flatChatList, triggerHighlight],
  );

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // ─── Render ─────────────────────────────────────────────────────

  return (
    <>
      <Stack
        width="100%"
        height="100%"
        direction="column"
        sx={{
          backgroundColor: theme.palette.neutral.main,
        }}
        justifyContent="space-between"
      >
        <ChatHeader
          onFilterSelect={handleFilterChange}
          lastUpdated={lastUpdated}
          serviceType={serviceType}
          serviceId={serviceId}
          section={section}
          onClose={handleClose}
          onFullscreenClick={onFullscreenClick}
          onSearchClick={handleOpenSearchDrawer}
          isDisabled={isDisabled}
        />
        <PinnedMessageBanner
          pinnedMessages={pinnedMessages}
          onViewAllClick={handleOpenPinnedDrawer}
          onUnpin={handleUnpinFromDrawer}
          onPinnedMessageClick={handlePinnedMessageClick}
        />
        <Box sx={{ position: "relative", flexGrow: 1, display: "flex" }}>
          <ChatMessageList
            flatChatList={flatChatList}
            firstItemIndex={firstItemIndex}
            virtuosoRef={virtuosoRef}
            isMessagesLoading={isLoadingInitial}
            resourceId={resourceId || ""}
            pinnedChatIdSet={pinnedChatIdSet}
            feedback={feedback}
            rating={rating}
            onMarkAsResolved={handleMarkAsResolved}
            onPinMessage={handlePinMessage}
            onAddToKB={handleAddToKB}
            onReply={handleReplyClick}
            onThreadClick={onThreadClick}
            onLoadMore={handleLoadMore}
            hasNextPage={!reachedOldest}
            isFetchingNextPage={isLoadingMore}
            highlightedChatId={highlightedChatId}
            editingMessageId={editingMessageId}
            onEditClick={handleEditClick}
            onEditSave={handleEditSave}
            onEditCancel={handleEditCancel}
            onAtBottomStateChange={handleAtBottomStateChange}
          />
          <NewMessagesPill virtuosoRef={virtuosoRef} />
        </Box>
        {!threadParentMessage && (
          <ChatInputSection
            key={serviceId}
            serviceType={serviceType}
            serviceId={serviceId}
            serviceSource={serviceSource}
            isDisabled={isDisabled}
            isTriageInProgress={isTriageInProgress}
            metaData={metaData}
            isSendingMessage={isSendingMessage}
            editorRef={editorRef}
            onManageParticipantsClick={(e: React.MouseEvent<HTMLElement>) =>
              setParticipantsAnchorEl(e.currentTarget)
            }
            onSend={handleInputSend}
          />
        )}
      </Stack>
      {kbDrawerOpen && (
        <KbArticleDrawer
          open={kbDrawerOpen}
          articleId={newKbArticleId}
          onClose={handleCloseKbDrawer}
        />
      )}
      {pinnedDrawerOpen && (
        <PinnedChatsDrawer
          open={pinnedDrawerOpen}
          onClose={handleClosePinnedDrawer}
          pinnedMessages={pinnedMessages}
          onUnpin={handleUnpinFromDrawer}
          onPinnedMessageClick={handlePinnedMessageClick}
        />
      )}
      {searchDrawerOpen && (
        <ChatSearchDrawer
          open={searchDrawerOpen}
          onClose={handleCloseSearchDrawer}
          serviceType={serviceType}
          serviceId={serviceId}
          onResultClick={handleSearchResultClick}
        />
      )}
      <ChatMembersPopover
        anchorEl={participantsAnchorEl}
        open={participantsPopoverOpen}
        onClose={() => setParticipantsAnchorEl(null)}
        serviceId={serviceId}
      />
    </>
  );
};
